import React, { useState, useEffect, useRef } from 'react';
import { Modal, Button } from '@Comp';
import { FiX, FiShield } from 'react-icons/fi';
import { inject } from 'mobx-react';
import './EmailVerificationModal.scss';

const EmailVerificationModal = inject('commonStore')(({
  isOpen,
  onClose,
  email,
  password,
  businessData,
  onSuccess,
  commonStore
}) => {
  const [code, setCode] = useState(['', '', '', '', '', '']);
  const [error, setError] = useState('');
  const [attempts, setAttempts] = useState(5);
  const [isVerifying, setIsVerifying] = useState(false);
  const [resendCooldown, setResendCooldown] = useState(0);
  const inputRefs = useRef([]);

  // Initialize refs
  useEffect(() => {
    inputRefs.current = inputRefs.current.slice(0, 6);
  }, []);

  // Auto-focus first input when modal opens
  useEffect(() => {
    if (isOpen && inputRefs.current[0]) {
      setTimeout(() => {
        inputRefs.current[0].focus();
      }, 100);
    }
  }, [isOpen]);

  // Countdown timer for resend
  useEffect(() => {
    let timer;
    if (resendCooldown > 0) {
      timer = setTimeout(() => {
        setResendCooldown(resendCooldown - 1);
      }, 1000);
    }
    return () => clearTimeout(timer);
  }, [resendCooldown]);

  // Auto-submit when all 6 digits are entered
  useEffect(() => {
    const isComplete = code.every(digit => digit !== '');
    if (isComplete && !isVerifying) {
      handleVerify();
    }
  }, [code]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleInputChange = (index, value) => {
    // Only allow numeric input
    if (value && !/^\d$/.test(value)) {
      return;
    }

    const newCode = [...code];
    newCode[index] = value;
    setCode(newCode);
    setError(''); // Clear error on new input

    // Auto-focus next input
    if (value && index < 5) {
      inputRefs.current[index + 1].focus();
    }
  };

  const handleKeyDown = (index, e) => {
    // Handle backspace
    if (e.key === 'Backspace') {
      if (!code[index] && index > 0) {
        // If current input is empty, focus previous and clear it
        inputRefs.current[index - 1].focus();
        const newCode = [...code];
        newCode[index - 1] = '';
        setCode(newCode);
      } else {
        // Clear current input
        const newCode = [...code];
        newCode[index] = '';
        setCode(newCode);
      }
    }
    // Handle left arrow
    else if (e.key === 'ArrowLeft' && index > 0) {
      inputRefs.current[index - 1].focus();
    }
    // Handle right arrow
    else if (e.key === 'ArrowRight' && index < 5) {
      inputRefs.current[index + 1].focus();
    }
  };

  const handlePaste = (e) => {
    e.preventDefault();
    const pastedData = e.clipboardData.getData('text').trim();

    // Only process if it's 6 digits
    if (/^\d{6}$/.test(pastedData)) {
      const newCode = pastedData.split('');
      setCode(newCode);
      setError('');
      // Focus last input
      inputRefs.current[5].focus();
    }
  };

  const handleVerify = async () => {
    const verificationCode = code.join('');

    if (verificationCode.length !== 6) {
      setError('Please enter all 6 digits');
      return;
    }

    if (attempts <= 0) {
      setError('Too many failed attempts. Please request a new code.');
      return;
    }

    setIsVerifying(true);
    setError('');

    try {
      // Step 1: Verify the email code (just verification, no storage)
      console.log('Step 1: Verifying email code...');
      const verifyResponse = await commonStore.verifyEmailCode(email, verificationCode);

      if (!verifyResponse || !verifyResponse.success) {
        throw new Error('Email verification failed');
      }

      // Step 2: Create business profile (BEFORE login, but DON'T join yet)
      console.log('Step 2: Creating business profile...');
      await commonStore.addBusinessProfile(
        businessData.businessName,
        businessData.businessEmailAddress,
        businessData.cipcRegNo,
        true  // skipJoin = true (login will fetch complete user)
      );

      // Step 3: Close modal and show main page loader
      console.log('Step 3: Closing modal, preparing to login...');
      if (onSuccess) {
        onSuccess();  // Close modal immediately
      }

      // Step 4: Call actual login API (this does EVERYTHING including loading profile)
      console.log('Step 4: Calling login API (handles all initialization)...');
      await commonStore.login(email, password);

      // Step 5: Success - all data loaded by login, redirect
      console.log('Step 5: Login successful, all data initialized, redirecting...');

      // Check if user needs to complete onboarding
      if (commonStore.user && commonStore.user.onboardingCompleted === false) {
        window.location.href = '/home/onboarding/step1';
      } else {
        window.location.href = '/home';
      }

    } catch (e) {
      setIsVerifying(false);
      console.error('Verification error:', e);

      // Decrement attempts
      const newAttempts = attempts - 1;
      setAttempts(newAttempts);

      // Handle different error types
      let errorMessage = 'Verification failed. Please try again.';

      if (e.response && e.response.data) {
        const errorData = e.response.data;

        if (errorData.message) {
          if (typeof errorData.message === 'string') {
            errorMessage = errorData.message;
          } else if (Array.isArray(errorData.message) && errorData.message[0]?.messages?.[0]?.message) {
            errorMessage = errorData.message[0].messages[0].message;
          }
        } else if (errorData.error) {
          errorMessage = errorData.error;
        }
      } else if (e.message) {
        errorMessage = e.message;
      }

      // Check for specific error types
      if (errorMessage.toLowerCase().includes('invalid verification code') ||
          errorMessage.toLowerCase().includes('invalid code')) {
        setError(`Invalid code. ${newAttempts} attempt${newAttempts !== 1 ? 's' : ''} remaining.`);
      } else if (errorMessage.toLowerCase().includes('expired')) {
        setError('Code has expired. Please request a new code.');
      } else if (errorMessage.toLowerCase().includes('too many')) {
        setError('Too many attempts. Please request a new code.');
        setAttempts(0);
      } else if (errorMessage.toLowerCase().includes('fraudulent') ||
                 errorMessage.toLowerCase().includes('cipc already exists')) {
        setError(errorMessage);
        setAttempts(0);
      } else if (newAttempts <= 0) {
        setError('Too many failed attempts. Please request a new code.');
      } else {
        setError(`${errorMessage}. ${newAttempts} attempt${newAttempts !== 1 ? 's' : ''} remaining.`);
      }

      // Clear code inputs
      setCode(['', '', '', '', '', '']);
      if (inputRefs.current[0]) {
        inputRefs.current[0].focus();
      }
    }
  };

  const handleResend = async () => {
    if (resendCooldown > 0) {
      return;
    }

    try {
      await commonStore.resendVerificationCode(email);
      setResendCooldown(60); // Start 60 second cooldown
      setAttempts(5); // Reset attempts
      setError('');
      setCode(['', '', '', '', '', '']); // Clear code
      if (inputRefs.current[0]) {
        inputRefs.current[0].focus();
      }
    } catch (e) {
      if (e.response && e.response.data) {
        const errorData = e.response.data;
        let errorMessage = 'Failed to resend code';

        if (errorData.message) {
          if (typeof errorData.message === 'string') {
            errorMessage = errorData.message;
          } else if (Array.isArray(errorData.message) && errorData.message[0]?.messages?.[0]?.message) {
            errorMessage = errorData.message[0].messages[0].message;
          }
        } else if (errorData.error) {
          errorMessage = errorData.error;
        }

        setError(errorMessage);
      } else {
        setError('Failed to resend code. Please try again.');
      }
    }
  };

  const handleManualVerify = (e) => {
    e.preventDefault();
    handleVerify();
  };

  return (
    <Modal isOpen={isOpen} toggle={onClose} className="email-verification-modal" backdrop="static">
      <div className="verification-modal-content">
        <button
          className="verification-close-btn"
          onClick={onClose}
          aria-label="Close modal"
          type="button"
        >
          <FiX size={24} />
        </button>

        <div className="verification-icon-wrapper">
          <FiShield className="verification-icon" size={48} />
        </div>

        <h2 className="verification-title">Verify your email address</h2>

        <p className="verification-description">
          Enter the code we've sent to <strong>{email}</strong>
        </p>

        <form onSubmit={handleManualVerify} className="verification-form">
          <div className="verification-code-inputs" onPaste={handlePaste}>
            {code.map((digit, index) => (
              <input
                key={index}
                ref={el => inputRefs.current[index] = el}
                type="text"
                inputMode="numeric"
                pattern="[0-9]"
                maxLength="1"
                value={digit}
                onChange={(e) => handleInputChange(index, e.target.value)}
                onKeyDown={(e) => handleKeyDown(index, e)}
                className={`verification-code-input ${error ? 'error' : ''}`}
                disabled={isVerifying}
                aria-label={`Digit ${index + 1}`}
              />
            ))}
          </div>

          {error && (
            <div className="verification-error" role="alert">
              {error}
            </div>
          )}

          <div className="verification-info">
            <p className="verification-expiry">It expires in 15 minutes</p>
            <p className="verification-attempts">
              Attempts remaining: {attempts}/5
            </p>
          </div>

          <Button
            type="submit"
            className="btn-verify"
            disabled={isVerifying || code.join('').length !== 6}
            block
          >
            {isVerifying ? 'Verifying...' : 'Verify Email'}
          </Button>
        </form>

        <div className="verification-resend">
          {resendCooldown > 0 ? (
            <p className="resend-cooldown">
              Resend available in {resendCooldown}s
            </p>
          ) : (
            <button
              type="button"
              className="resend-link"
              onClick={handleResend}
            >
              Send a new code
            </button>
          )}
        </div>
      </div>
    </Modal>
  );
});

export default EmailVerificationModal;
